import React from 'react';
import i18n from 'i18next';
import { Helmet } from 'react-helmet-async';
import { isEditorActive, withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { layoutServiceFactory } from './lib/layout-service-factory';
import config from './temp/config';
import Layout from './Layout';
import { EventEnum } from './enums/event.enum';
import { SP_QUERY_STRINGS } from './constants/special-offers-page.constants';
import { DL_QUERY_STRINGS } from './constants/dealerLocator.constants';
import { QueryParamsConst } from './constants/query-params.constants';
import NotFound from './NotFound';

/* eslint-disable no-console */

// Dynamic route handler for Sitecore items.
// Because JSS app routes are defined in Sitecore, traditional static React routing isn't enough -
// we need to be able to load dynamic route data from Sitecore after the client side route changes.
// So react-router delegates all route rendering to this handler, which attempts to get the right
// route data from Sitecore - and if none exists, renders the not found component.

class RouteHandler extends React.Component {
  constructor(props) {
    super(props);
    // tell i18next to sync its current language with the route language
    this.updateLanguage();
  }

  componentDidMount() {
    // If we are not using SSR we have to load layout data
    if (!this.props.isSSR) {
      this.updateLayoutData();
    }
    this.componentIsMounted = true;
  }

  componentWillUnmount() {
    this.componentIsMounted = false;
  }

  extractSearchQueryParams = () => {
    const searchParams = this.props?.location?.search || '';

    const params = new URLSearchParams(searchParams);
    const paramObj = {};
    const whitelistedQueryParamKeys = Object.values(QueryParamsConst);
    for (const value of params.keys()) {
      if (whitelistedQueryParamKeys.includes(value)) {
        paramObj[value] = params.get(value);
      }
    }
    return paramObj;
  };

  /**
   * Loads route data from Sitecore Layout Service into state.routeData
   */
  updateLayoutData() {
    const language = this.getLanguage();
    const siteName = this.props.sitecoreContext?.site?.name || config.jssAppName;
    const extraQueryParams = this.extractSearchQueryParams();


    window.dispatchEvent(new CustomEvent(EventEnum.ADD_LOADER_QUEUE_ITEM));
    let sitecoreRoutePath = this.props.route || '/';
    if (!sitecoreRoutePath.startsWith('/')) {
      sitecoreRoutePath = `/${sitecoreRoutePath}`;
    }

    // instantiate the dictionary service.
    const layoutServiceInstance = layoutServiceFactory.create(siteName, extraQueryParams);

    // get the route data for the new route
    layoutServiceInstance.fetchLayoutData(sitecoreRoutePath, language).then((routeData) => {
      // Unhide the header on page navigation.
      // scenario: page with navigation bar, scroll till header hide and navigate to new page, header was still hidden, as the class was not removed
      // Since the data reaches here first and this is first line of indication that data to update new page is received
      // adding the below code to make the header visible
      if (typeof document !== 'undefined') {
        const header = document.querySelector('#root > header > .header > #sticky-top-wrapper > .navigation');
        header && header.classList.remove('sticky-element--hide');
      }
      // checking if specific scroll to a component (ex: https://.....#model-listing) is present in the url.
      // If not, Scroll to top of page after api data is received.
      if (!sitecoreRoutePath?.includes?.('#')) {
        typeof window !== 'undefined' &&
          window.scrollTo({
            top: 0,
          });
      }
      this.props.updateSitecoreContext(routeData);
      window.dispatchEvent(new CustomEvent(EventEnum.REMOVE_LOADER_QUEUE_ITEM));
    });
  }

  getLanguage() {
    return this.props.language || this.props.sitecoreContext.language || config.defaultLanguage;
  }

  /**
   * Updates the current app language to match the route data.
   */
  updateLanguage() {
    const newLanguage = this.getLanguage();

    if (i18n.language !== newLanguage) {
      i18n.changeLanguage(newLanguage);
    }
  }

  hasDiffOnlyInExcludedParams = (newQueryParams, existingSearchParams) => {
    const excludedSearchParams = [
      DL_QUERY_STRINGS.SEARCH_QUERY,
      DL_QUERY_STRINGS.PLACE_ID,
      DL_QUERY_STRINGS.USER_LATITUDE,
      DL_QUERY_STRINGS.USER_LONGITUDE,
      DL_QUERY_STRINGS.COUNTRY,
      DL_QUERY_STRINGS.REGION,
      DL_QUERY_STRINGS.EQUIPMENT_FILTERS,
      DL_QUERY_STRINGS.SERVICES_FILTERS,
      DL_QUERY_STRINGS.SELECTED_DEALER,
      SP_QUERY_STRINGS.PAGE,
      SP_QUERY_STRINGS.CATEGORY,
      SP_QUERY_STRINGS.LINE,
      SP_QUERY_STRINGS.SERIES,
      SP_QUERY_STRINGS.MODEL,
    ];
    const newSearchParamsObj = new URLSearchParams(newQueryParams);
    const currentQueryParamsObj = new URLSearchParams(existingSearchParams);

    //Remove excluded searchParams and compare changes
    excludedSearchParams.forEach((param) => {
      newSearchParamsObj.delete(param);
      currentQueryParamsObj.delete(param);
    });

    return newSearchParamsObj?.toString() === currentQueryParamsObj?.toString();
  };

  componentDidUpdate(previousProps) {
    const existingRoute = previousProps.url;
    const newRoute = this.props.url;
    const existingSearchParams = previousProps?.location?.search || '';
    const newSearchParams = this.props?.location?.search || '';

    // don't change state (refetch route data) if the route has not changed
    if (existingRoute + existingSearchParams === newRoute + newSearchParams) {
      return;
    }

    // don't change state (refetch route data) if the route has not changed
    if (existingRoute === newRoute && this.hasDiffOnlyInExcludedParams(newSearchParams, existingSearchParams)) {
      return;
    }

    // if in Sitecore editor - force reload instead of route data update
    // avoids confusing Sitecore's editing JS
    if (isEditorActive()) {
      window.location.assign(newRoute);
      return;
    }

    this.updateLanguage();
    this.updateLayoutData();
  }

  render() {
    const layoutData = this.props.sitecoreContext;
    // const layoutData = this.extractRouteData();

    // Note: this is client-side only 404 handling. Server-side 404 handling is the responsibility
    // of the server being used (i.e. node-headless-ssr-proxy and Sitecore intergrated rendering know how to send 404 status codes).
    // `route` is null in case if route is not found
    if (layoutData.route === null) {
      return (
        <div>
          <Helmet>
            <title>{i18n.t('Page not found')}</title>
          </Helmet>
          <NotFound context={layoutData} />
        </div>
      );
    }

    // Don't render anything if the route data or dictionary data is not fully loaded yet.
    // This is a good place for a "Loading" component, if one is needed.
    if (!layoutData.route) {
      return null;
    }

    // Render the app's root structural layout
    return <Layout route={layoutData.route} />;
  }
}

export default withSitecoreContext({ updatable: true })(RouteHandler);
